import React, { useState } from 'react';
import { FaMobileAlt, FaCalendarAlt, FaUser, FaExclamationTriangle, FaLaptop, FaMapMarkerAlt, FaBell, FaCheckCircle } from 'react-icons/fa';

const DeviceBreachForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateDiscovered: '',
    timeDiscovered: '',
    dateOccurred: '',
    timeOccurred: '',
    discoveredBy: '',
    incidentType: '',
    deviceTypes: [],
    deviceDetails: '',
    estimatedPatientCount: '',
    dataEncrypted: false,
    dataTypes: [],
    location: '',
    circumstances: '',
    incidentDescription: '',
    immediateActions: [],
    deviceRecovered: false,
    recoveryDetails: '',
    lawEnforcementContacted: false,
    insuranceClaim: false,
    notificationsRequired: [],
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
    priority: 'high'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const incidentTypes = [
    'Device Event',
    'Device Event',
    'Device Left Unattended',
    'Device Stolen from Vehicle',
    'Device Stolen from Office',
    'Device Lost in Transit',
    'Device Discarded Improperly'
  ];

  const deviceTypeOptions = [
    'Laptop Computer',
    'Desktop Computer',
    'Tablet',
    'Smartphone',
    'USB Drive/Flash Drive',
    'External Hard Drive',
    'CD/DVD',
    'Paper Records',
    'Backup Tapes',
    'Server',
    'Other'
  ];

  const dataTypeOptions = [
    'Patient Names',
    'Social Security Numbers',
    'Medical Record Numbers',
    'Health Insurance Information',
    'Medical History',
    'Treatment Records',
    'Billing Information',
    'Contact Information',
    'System Credentials',
    'Other PHI'
  ];

  const immediateActionOptions = [
    'Contacted law enforcement',
    'Filed insurance claim',
    'Changed system passwords',
    'Disabled remote access',
    'Attempted remote wipe',
    'Notified IT department',
    'Documented incident',
    'Searched premises'
  ];

  const notificationOptions = [
    'Practice Management',
    'IT Department',
    'Legal Counsel',
    'Insurance Provider',
    'Law Enforcement',
    'Patients (if required)',
    'Staff',
    'Regulatory Bodies'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dateDiscovered) newErrors.dateDiscovered = 'Date discovered is required';
    if (!formData.discoveredBy.trim()) newErrors.discoveredBy = 'Discovered by is required';
    if (!formData.incidentType) newErrors.incidentType = 'Event type is required';
    if (formData.deviceTypes.length === 0) newErrors.deviceTypes = 'At least one device type must be selected';
    if (!formData.incidentDescription.trim()) newErrors.incidentDescription = 'Event description is required';
    if (!formData.reportedBy.trim()) newErrors.reportedBy = 'Reporter name is required';
    if (!formData.reportedByEmail.trim()) newErrors.reportedByEmail = 'Reporter email is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await onSubmit({
        ...formData,
        formType: 'ITBreach',
        subType: 'DeviceTheftLoss',
        submittedAt: new Date(),
        severity: formData.priority
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaMobileAlt className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Device Event Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report device activity involving patient data requiring review
          </p>
          <div className="mt-4 inline-flex items-center rounded-md bg-orange-100 px-3 py-1 text-sm font-medium text-orange-800">
            Priority: High
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Event Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Event Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When the device event was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateDiscovered"
                        name="dateDiscovered"
                        type="date"
                        value={formData.dateDiscovered}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Time Discovered
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeDiscovered"
                        name="timeDiscovered"
                        type="time"
                        value={formData.timeDiscovered}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Date Occurred (if different)
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateOccurred"
                        name="dateOccurred"
                        type="date"
                        value={formData.dateOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Time Occurred
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeOccurred"
                        name="timeOccurred"
                        type="time"
                        value={formData.timeOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title of person who discovered the event"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="incidentType" className="block text-sm/6 font-medium text-gray-900">
                      Type of Event <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="incidentType"
                        name="incidentType"
                        value={formData.incidentType}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentType ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select event type...</option>
                        {incidentTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      {errors.incidentType && <p className="mt-1 text-sm text-red-600">{errors.incidentType}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Device Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaLaptop className="size-5 text-gray-400" />
                  Device Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Details about the lost or stolen device
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">
                    Types of Devices <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {deviceTypeOptions.map(device => (
                      <div key={device} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={device}
                              checked={formData.deviceTypes.includes(device)}
                              onChange={(e) => handleMultiSelectChange(e, 'deviceTypes')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{device}</label>
                      </div>
                    ))}
                  </div>
                  {errors.deviceTypes && <p className="mt-2 text-sm text-red-600">{errors.deviceTypes}</p>}
                </div>

                <div className="mt-6">
                  <label htmlFor="deviceDetails" className="block text-sm/6 font-medium text-gray-900">
                    Device Details
                  </label>
                  <div className="mt-2">
                    <textarea
                      id="deviceDetails"
                      name="deviceDetails"
                      rows={3}
                      value={formData.deviceDetails}
                      onChange={handleInputChange}
                      placeholder="Provide specific details: make, model, serial number, asset tags, etc."
                      className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                    />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="estimatedPatientCount" className="block text-sm/6 font-medium text-gray-900">
                      Estimated Patients Affected
                    </label>
                    <div className="mt-2">
                      <input
                        id="estimatedPatientCount"
                        name="estimatedPatientCount"
                        type="number"
                        value={formData.estimatedPatientCount}
                        onChange={handleInputChange}
                        placeholder="Enter number if patient data was on device"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex gap-3">
                      <div className="flex h-6 shrink-0 items-center">
                        <div className="group grid size-4 grid-cols-1">
                          <input
                            type="checkbox"
                            name="dataEncrypted"
                            checked={formData.dataEncrypted}
                            onChange={handleInputChange}
                            className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                          />
                          <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                            <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                          </svg>
                        </div>
                      </div>
                      <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Data on device was encrypted</label>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Types of Data on Device</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {dataTypeOptions.map(dataType => (
                      <div key={dataType} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={dataType}
                              checked={formData.dataTypes.includes(dataType)}
                              onChange={(e) => handleMultiSelectChange(e, 'dataTypes')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{dataType}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Location & Circumstances */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaMapMarkerAlt className="size-5 text-gray-400" />
                  Location & Circumstances
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Where and how the event occurred
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="location" className="block text-sm/6 font-medium text-gray-900">
                      Location of Event
                    </label>
                    <div className="mt-2">
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Where did the device event occur?"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="circumstances" className="block text-sm/6 font-medium text-gray-900">
                      Circumstances
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="circumstances"
                        name="circumstances"
                        rows={3}
                        value={formData.circumstances}
                        onChange={handleInputChange}
                        placeholder="Describe the circumstances surrounding the device event"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                      Detailed Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="incidentDescription"
                        name="incidentDescription"
                        rows={4}
                        value={formData.incidentDescription}
                        onChange={handleInputChange}
                        placeholder="Provide a detailed description of what happened, when it was discovered, and timeline of events..."
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Actions */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Response Actions
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Immediate actions taken in response
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Immediate Actions Taken</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {immediateActionOptions.map(action => (
                      <div key={action} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={action}
                              checked={formData.immediateActions.includes(action)}
                              onChange={(e) => handleMultiSelectChange(e, 'immediateActions')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{action}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 space-y-6">
                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="deviceRecovered"
                          checked={formData.deviceRecovered}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Device has been recovered</label>
                  </div>

                  {formData.deviceRecovered && (
                    <div>
                      <label htmlFor="recoveryDetails" className="block text-sm/6 font-medium text-gray-900">
                        Recovery Details
                      </label>
                      <div className="mt-2">
                        <textarea
                          id="recoveryDetails"
                          name="recoveryDetails"
                          rows={3}
                          value={formData.recoveryDetails}
                          onChange={handleInputChange}
                          placeholder="Describe how and when the device was recovered"
                          className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="lawEnforcementContacted"
                          checked={formData.lawEnforcementContacted}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Law enforcement has been contacted</label>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="insuranceClaim"
                          checked={formData.insuranceClaim}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Insurance claim has been filed</label>
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBell className="size-5 text-gray-400" />
                  Required Notifications
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select required notifications
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Notifications Required</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {notificationOptions.map(notification => (
                      <div key={notification} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={notification}
                              checked={formData.notificationsRequired.includes(notification)}
                              onChange={(e) => handleMultiSelectChange(e, 'notificationsRequired')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{notification}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Your Title
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Your job title"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeviceBreachForm;
